
import { supabase } from "@/integrations/supabase/client";
import { Lead } from "@/types";
import { toast } from "@/hooks/use-toast";

export const fetchLeadsByDealer = async (dealerId: string): Promise<Lead[]> => {
  try {
    console.log("Fetching leads for dealer:", dealerId);
    const { data, error } = await supabase
      .from('leads')
      .select('*')
      .eq('dealer_id', dealerId)
      .order('created_at', { ascending: false });
    
    if (error) {
      console.error("Error fetching leads:", error);
      throw new Error(`Supabase error: ${error.message} (code: ${error.code})`);
    }
    
    console.log(`Retrieved ${data.length} leads from Supabase`);
    return data || [];
  } catch (error) {
    console.error("Error in fetchLeadsByDealer:", error);
    toast({
      title: "Failed to load leads",
      description: error instanceof Error ? error.message : "Unknown error occurred",
      variant: "destructive",
    });
    return [];
  }
};

/**
 * Find a lead by phone number
 * @param phone The phone number to search for
 * @returns A promise with the lead or an error
 */
export const findLeadByPhone = async (phone: string): Promise<{ 
  success: boolean; 
  lead?: any; 
  error?: string 
}> => {
  try {
    // Normalize the phone number to ensure consistent formatting
    let normalizedPhone = phone.trim();
    
    // If the phone already has a plus, use it as is, otherwise add it
    if (!normalizedPhone.startsWith('+')) {
      normalizedPhone = `+${normalizedPhone.replace(/^\+/, '')}`;
    }
    
    console.log(`Finding lead for phone number: ${normalizedPhone}`);
    
    const { data, error } = await supabase
      .from('leads')
      .select('id, name, phone, email, status, dealer_id')
      .eq('phone', normalizedPhone)
      .maybeSingle();
    
    if (error) throw error;
    
    return {
      success: true,
      lead: data || null
    };
  } catch (error) {
    console.error('Error finding lead by phone:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'An unknown error occurred'
    };
  }
};

/**
 * Find leads by conversation data
 * @param conversationId ElevenLabs conversation ID
 * @returns A promise with the lead or an error
 */
export const findLeadByConversationId = async (conversationId: string): Promise<{
  success: boolean;
  lead?: any;
  error?: string
}> => {
  try {
    // First, check for calls that have this conversation ID in their meta field
    const { data, error } = await supabase
      .from('calls')
      .select('lead_id')
      .like('meta->conversation_id', conversationId)
      .maybeSingle();
    
    if (error) throw error;
    
    if (data?.lead_id) {
      // If we found a call with this conversation ID, get the lead
      const { data: leadData, error: leadError } = await supabase
        .from('leads')
        .select('id, name, phone, email, status, dealer_id')
        .eq('id', data.lead_id)
        .single();
      
      if (leadError) throw leadError;
      
      return {
        success: true,
        lead: leadData
      };
    }
    
    return {
      success: true,
      lead: null
    };
  } catch (error) {
    console.error('Error finding lead by conversation ID:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'An unknown error occurred'
    };
  }
};
